// Edit planner for database lifecycle ops — CREATE / ALTER / DROP DATABASE
// (v2 Slice G1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice G1.
//
// All three ops append a fresh statement after the source statement (DSE2-7:
// no coalescing by default). Per-engine support / rejection lives in the
// dialect synthesizer (`dialect.database.ts`).

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAlterDatabase,
  synthesizeCreateDatabase,
  synthesizeDropDatabase,
} from './dialect.database';

type DatabaseLifecycleEdit = Extract<
  DdlEdit,
  {
    op: 'create-database' | 'alter-database' | 'drop-database';
  }
>;

export function planDatabaseEdit(
  ctx: DdlEditCtx,
  edit: DatabaseLifecycleEdit,
): readonly PlannedByteEdit[] {
  const synth = synthForDatabase(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: edit.op,
  }];
}

function synthForDatabase(ctx: DdlEditCtx, edit: DatabaseLifecycleEdit): string {
  switch (edit.op) {
    case 'create-database':
      return synthesizeCreateDatabase(ctx.engine, edit.database);
    case 'alter-database':
      return synthesizeAlterDatabase(
        ctx.engine,
        edit.databaseName,
        edit.quoted ?? false,
        edit.mysqlSchema,
        edit.change,
      );
    case 'drop-database':
      return synthesizeDropDatabase(ctx.engine, {
        databaseName: edit.databaseName,
        quoted: edit.quoted ?? false,
        ifExists: edit.ifExists ?? false,
        mysqlSchema: edit.mysqlSchema,
        pgsqlForce: edit.pgsqlForce,
        oraclePdbOptionsText: edit.oraclePdbOptionsText,
      });
  }
}
